import { Injectable } from '@angular/core';
import { EMPTY, filter, map, Observable, tap } from 'rxjs';
import { CONNECTION_STATE_CHANGE, PubSub } from '@aws-amplify/pubsub';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { environment } from '@environments/environment';
import { AwsIotWeightMessage } from '@app/models/mqtt/weight-station-message';

// The scales publish to AWS IoT Core; the browser subscribes over `wss://` with
// temporary credentials from a Cognito identity pool (guest access — the page is
// already behind the app's own authentication). The identity pool's role only
// allows connect/subscribe/receive on the weigh-station topic tree, never publish.
// See _infra/provision-production-ui-iot.sh.
@Injectable({
  providedIn: 'root',
})
export class AwsIotMqttService {
  private pubSub: PubSub | null = null;

  /** False when the environment has no identity pool / endpoint yet — callers should degrade instead of failing. */
  public readonly isConfigured = !!environment.awsIot.identityPoolId && !!environment.awsIot.endpoint;

  /**
   * Subscribe to a topic on AWS IoT Core, emitting one `AwsIotWeightMessage` per
   * message. Malformed payloads are dropped instead of breaking the stream.
   * Returns an empty stream when the broker isn't configured for this environment.
   */
  public subscribe(topic: string): Observable<AwsIotWeightMessage> {
    const client = this.getClient();
    if (!client) {
      return EMPTY;
    }

    let isFirstMessage = true;

    return client.subscribe({ topics: [topic] }).pipe(
      tap({
        subscribe: () => console.log(`[AWS IoT] Subscribing to topic "${topic}"`),
      }),
      map((message) => this.toWeightMessage(message)),
      filter((message): message is AwsIotWeightMessage => message !== null),
      tap((message) => {
        if (isFirstMessage) {
          isFirstMessage = false;
          console.log(`[AWS IoT] First message from "${topic}":`, message);
        }
      }),
    );
  }

  private getClient(): PubSub | null {
    if (!this.isConfigured) {
      console.error('AWS IoT is not configured for this environment (environment.awsIot.identityPoolId is empty)');
      return null;
    }

    if (!this.pubSub) {
      // Guest (unauthenticated) credentials — no login; IoT authorizes the
      // connection through the scoped IAM role attached to the identity pool.
      Amplify.configure({
        Auth: {
          Cognito: {
            identityPoolId: environment.awsIot.identityPoolId,
            allowGuestAccess: true,
          },
        },
      });

      const clientId = 'production-ui-' + Math.random().toString(16).substring(2, 10);
      console.log(`[AWS IoT] Connecting to ${environment.awsIot.endpoint} as "${clientId}"`);
      this.listenToConnectionState();

      this.pubSub = new PubSub({
        region: environment.awsIot.region,
        endpoint: environment.awsIot.endpoint,
        // Must match the `client/production-ui-*` resource in the guest IoT policy.
        clientId,
      });
    }

    return this.pubSub;
  }

  /** Logs the broker connection lifecycle (Connecting / Connected / ConnectionDisrupted / ...). */
  private listenToConnectionState(): void {
    Hub.listen('pubsub', ({ payload }) => {
      if (payload.event !== CONNECTION_STATE_CHANGE) {
        return;
      }

      const state = this.readConnectionState(payload.data);
      console.log(`[AWS IoT] Connection state: ${state}`);
    });
  }

  private readConnectionState(data: unknown): string {
    if (typeof data !== 'object' || data === null) {
      return 'Unknown';
    }

    const connectionState = (data as Record<string, unknown>)['connectionState'];
    return typeof connectionState === 'string' ? connectionState : 'Unknown';
  }

  /** Narrows the broker's untyped payload, discarding anything that isn't a usable reading. */
  private toWeightMessage(message: Record<string | symbol, unknown>): AwsIotWeightMessage | null {
    const weight = message['weight'];
    const unit = message['unit'];

    if (typeof weight !== 'number' || Number.isNaN(weight) || typeof unit !== 'string') {
      console.warn('Discarded malformed AWS IoT weight message:', message);
      return null;
    }

    const assetId = message['assetId'];
    const timestamp = message['timestamp'];

    return {
      assetId: typeof assetId === 'string' ? assetId : '',
      weight,
      unit,
      timestamp: typeof timestamp === 'string' ? timestamp : '',
    };
  }
}
